import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, Mail } from "lucide-react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { formatINR } from "@/data/tours";
import { itineraryPdfBase64 } from "@/lib/itineraryPdf";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

export const EmailItineraryDialog = ({ open, onOpenChange, pdfOptions }) => {
  const { tour, departure } = pdfOptions;
  const [form, setForm] = useState({ name: "", email: "", phone: "" });
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (form.name.trim().length < 2) return toast.error("Please enter your name.");
    if (!EMAIL_RE.test(form.email.trim())) return toast.error("Please enter a valid email address.");
    if (form.phone && !/^\+?\d{10,13}$/.test(form.phone.replace(/[\s-]/g, ""))) return toast.error("Please enter a valid mobile number.");
    setBusy(true);
    try {
      const pdf = await itineraryPdfBase64(pdfOptions);
      const price = departure?.price ?? tour.startingPrice;
      await axios.post(`${API}/itinerary/email`, {
        name: form.name.trim(),
        email: form.email.trim(),
        phone: form.phone.replace(/[\s-]/g, ""),
        tour_slug: tour.slug,
        tour_title: tour.title,
        tour_code: tour.code || "",
        days: tour.days,
        departure_date: departure?.date || "",
        price_text: price == null ? "Price on request" : formatINR(price).replace("₹", "Rs. "),
        pdf_base64: pdf,
      });
      toast.success(`Itinerary sent to ${form.email.trim()}. Please check your inbox.`);
      onOpenChange(false);
    } catch (err) {
      toast.error(err.response?.data?.detail && typeof err.response.data.detail === "string" ? err.response.data.detail : "Couldn't send the email. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="email-itinerary-dialog">
        <DialogHeader>
          <DialogTitle className="font-serif-display text-2xl text-[var(--navy)]">Email this itinerary</DialogTitle>
          <DialogDescription className="text-[13px]">
            We'll email you the {tour.title} PDF{departure ? ` for the ${departure.date} departure` : ""}.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-3 mt-1">
          <Input value={form.name} onChange={set("name")} placeholder="Your name*" className="h-11" data-testid="email-itinerary-name" />
          <Input value={form.email} onChange={set("email")} type="email" placeholder="Email address*" className="h-11" data-testid="email-itinerary-email" />
          <Input value={form.phone} onChange={set("phone")} placeholder="Mobile number (optional)" className="h-11" data-testid="email-itinerary-phone" />
          {!departure && tour.dates?.length > 0 && (
            <p className="text-[12px] text-gray-500" data-testid="email-itinerary-no-date">Tip: choose a departure date first to get a dated, priced itinerary.</p>
          )}
          <Button type="submit" disabled={busy} className="w-full h-11 bg-[var(--yellow)] hover:bg-[var(--yellow-2)] text-[var(--navy)] hover:text-white font-bold" data-testid="email-itinerary-submit">
            {busy ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Sending...</> : <><Mail className="w-4 h-4 mr-2" /> Send itinerary</>}
          </Button>
          <p className="text-[11px] text-gray-400 text-center">Our travel expert may call you to help plan your trip.</p>
        </form>
      </DialogContent>
    </Dialog>
  );
};
